import React, { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { VideoItem } from '../../types';
import { getVideoUrl, isHlsUrl, searchHighlights, searchVideos } from '../../utils/video-utils';

export type { VideoItem } from '../../types';

export interface VideoCarousel {
  id: string;
  title: string;
  videos: VideoItem[];
}

interface SortableVideoRowProps {
  video: VideoItem;
  index: number;
  onRemove: () => void;
}

function SortableVideoRow({ video, index, onRemove }: SortableVideoRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: video.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      elevation={isDragging ? 2 : 0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        bgcolor: 'grey.50',
        borderRadius: 1,
        '&:hover': { bgcolor: 'grey.100' },
      }}
    >
      <Box {...listeners} sx={{ cursor: 'grab', display: 'flex', flexShrink: 0 }}>
        <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </Box>
      <Typography
        variant="caption"
        sx={{
          width: 20,
          textAlign: 'center',
          color: 'text.secondary',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Typography>
      <Box
        component="img"
        src={video.thumbnail}
        alt={video.title}
        sx={{
          width: 60,
          height: 34,
          objectFit: 'cover',
          borderRadius: 0.5,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
          {video.title}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {video.duration}
      </Typography>
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          color: 'text.secondary',
          '&:hover': { color: 'error.main', bgcolor: 'error.lighter' },
          width: 24,
          height: 24,
          flexShrink: 0,
        }}
      >
        <DeleteIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Paper>
  );
}

function VideoRowOverlay({ video }: { video: VideoItem }) {
  return (
    <Paper
      elevation={4}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        bgcolor: 'background.paper',
        borderRadius: 1,
        width: 300,
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Box
        component="img"
        src={video.thumbnail}
        alt={video.title}
        sx={{
          width: 60,
          height: 34,
          objectFit: 'cover',
          borderRadius: 0.5,
        }}
      />
      <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: 13 }}>
        {video.title}
      </Typography>
    </Paper>
  );
}

interface VideoPreviewPlayerProps {
  video: VideoItem;
}

function VideoPreviewPlayer({ video }: VideoPreviewPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoUrl = getVideoUrl(video);

  useEffect(() => {
    if (!videoUrl) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }

    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (!isHlsUrl(videoUrl)) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }

    if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = videoUrl;
      return () => {
        videoEl.removeAttribute('src');
        videoEl.load();
      };
    }

    if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(videoUrl);
      hls.attachMedia(videoEl);
      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }
  }, [videoUrl]);

  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 480,
        aspectRatio: '16/9',
        bgcolor: 'black',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 2,
      }}
    >
      {videoUrl ? (
        <Box
          component="video"
          ref={videoRef}
          src={isHlsUrl(videoUrl) ? undefined : videoUrl}
          poster={video.thumbnail}
          playsInline
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <Box
          component="img"
          src={video.thumbnail}
          alt={video.title}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
      {videoUrl && (
        <IconButton
          onClick={handlePlayPause}
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            bgcolor: 'rgba(0,0,0,0.6)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
      )}
    </Box>
  );
}

interface VideoDetailsModalProps {
  open: boolean;
  onClose: () => void;
  video: VideoItem | null;
}

const HIDDEN_KEYS = new Set([
  'durationInMs',
  'durationInMS',
  'channels',
  'rating',
  'videoUrl',
  'webviewUrl',
]);

const KEY_TO_SWEDISH: Record<string, string> = {
  id: 'ID',
  title: 'Titel',
  description: 'Beskrivning',
  thumbnail: 'Miniatyrbild',
  thumbnailUrl: 'Miniatyrbild',
  duration: 'Längd',
  durationAsText: 'Längd',
  durationInSeconds: 'Längd (sekunder)',
  date: 'Datum',
  channelName: 'Kanal',
  category: 'Kategori',
  url: 'URL',
  hlsUrl: 'HLS-URL',
  streamUrl: 'Ström-URL',
};

export function VideoDetailsModal({ open, onClose, video }: VideoDetailsModalProps) {
  if (!video) return null;

  // Get all properties from video object, excluding hidden keys
  const videoEntries = Object.entries(video).filter(
    ([key, value]) => !HIDDEN_KEYS.has(key) && value !== undefined && value !== null && value !== ''
  );

  const formatValue = (value: unknown): string => {
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nej';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const formatKey = (key: string): string => {
    return (
      KEY_TO_SWEDISH[key] ??
      key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    );
  };

  const isThumbnailKey = (key: string): boolean => key === 'thumbnail' || key === 'thumbnailUrl';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Videoinformation</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Video Player */}
        <Box sx={{ mb: 2 }}>
          <VideoPreviewPlayer video={video} />
        </Box>

        {/* Title */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          {video.title}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* All Properties */}
        <Stack spacing={1.5}>
          {videoEntries.map(([key, value]) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              >
                {formatKey(key)}
              </Typography>
              {isThumbnailKey(key) && typeof value === 'string' ? (
                <Box
                  component="img"
                  src={value}
                  alt={formatKey(key)}
                  sx={{
                    maxWidth: 200,
                    maxHeight: 120,
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: typeof value === 'object' ? 'pre-wrap' : 'normal',
                    fontFamily: typeof value === 'object' ? 'monospace' : 'inherit',
                    fontSize: typeof value === 'object' ? 12 : 'inherit',
                  }}
                >
                  {formatValue(value)}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Stäng
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getVideoCreatedDate(video: VideoItem): string | null {
  const rawDate = video.date;
  if (!rawDate) return null;

  const date =
    typeof rawDate === 'object' &&
    rawDate !== null &&
    'toDate' in rawDate &&
    typeof rawDate.toDate === 'function'
      ? rawDate.toDate()
      : new Date(rawDate as string | number | Date);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Where the modal searches: Fotboll Play videos, or the team's match event clips. */
type SearchSource = 'fotbollplay' | 'matchEvent';

interface VideoCarouselModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (carousel: VideoCarousel) => void;
  editCarousel?: VideoCarousel | null;
  channels?: string[];
  /** The app's league; match event clips can't be searched without one. */
  league?: string;
  teamAbbrv?: string;
}

function VideoCarouselModal({
  open,
  onClose,
  onSave,
  editCarousel,
  channels,
  league,
  teamAbbrv,
}: VideoCarouselModalProps) {
  const [title, setTitle] = useState(editCarousel?.title || '');
  const [searchSource, setSearchSource] = useState<SearchSource>('fotbollplay');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<VideoItem[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<VideoItem[]>(editCarousel?.videos || []);
  const [isSearching, setIsSearching] = useState(false);
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [detailsVideo, setDetailsVideo] = useState<VideoItem | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!open) return;

    setTitle(editCarousel?.title || '');
    setSearchSource('fotbollplay');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedVideos(editCarousel?.videos || []);
    setActiveVideo(null);
    setDetailsVideo(null);
  }, [editCarousel, open]);

  // Debounced realtime search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const canSearch =
      searchSource === 'matchEvent' ? !!league && !!teamAbbrv : !!channels && channels.length > 0;

    if (!canSearch) {
      setSearchResults([]);
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Ignore a search that finishes after the query or source changed
    let isStale = false;

    // Set new debounced search
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results =
          searchSource === 'matchEvent'
            ? await searchHighlights(searchQuery, { league, teamAbbrv })
            : await searchVideos(searchQuery, { channels });
        if (isStale) return;
        // Filter out already selected videos
        const filteredResults = results.filter((r) => !selectedVideos.some((s) => s.id === r.id));
        setSearchResults(filteredResults);
      } catch (error) {
        if (isStale) return;
        console.error('Failed to search videos:', error);
        setSearchResults([]);
      } finally {
        if (!isStale) setIsSearching(false);
      }
    }, 300);

    return () => {
      isStale = true;
      setIsSearching(false);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, selectedVideos, channels, searchSource, league, teamAbbrv]);

  const handleAddVideo = (video: VideoItem) => {
    setSelectedVideos((prev) => [...prev, video]);
    setSearchResults((prev) => prev.filter((v) => v.id !== video.id));
  };

  const handleRemoveVideo = (videoId: string) => {
    setSelectedVideos((prev) => prev.filter((v) => v.id !== videoId));
  };

  const handleDragStart = (event: DragStartEvent) => {
    const video = selectedVideos.find((v) => v.id === event.active.id);
    setActiveVideo(video || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedVideos.findIndex((v) => v.id === active.id);
      const newIndex = selectedVideos.findIndex((v) => v.id === over.id);
      setSelectedVideos(arrayMove(selectedVideos, oldIndex, newIndex));
    }

    setActiveVideo(null);
  };

  const handleSave = () => {
    if (!title.trim() || selectedVideos.length === 0) return;

    onSave({
      id: editCarousel?.id || `carousel-${Date.now()}`,
      title: title.trim(),
      videos: selectedVideos,
    });

    handleClose();
  };

  const handleClose = () => {
    setTitle('');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedVideos([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {editCarousel ? 'Redigera videokarusell' : 'Skapa ny videokarusell'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Title Input */}
          <TextField
            label="Karuselltitel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            placeholder="T.ex. Senaste höjdpunkterna"
          />

          {/* Search Input */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Sök videor
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={searchSource}
                onChange={(_, value: SearchSource | null) => {
                  if (!value) return;
                  setSearchSource(value);
                  setSearchResults([]);
                }}
              >
                <ToggleButton value="fotbollplay">FotbollPlay-videor</ToggleButton>
                <ToggleButton value="matchEvent" disabled={!league}>
                  Matchhöjdpunkter
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {!league && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Appen har ingen liga, så matchhöjdpunkter kan inte sökas.
              </Typography>
            )}
            {searchSource === 'matchEvent' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Lagets egna händelser i ligan som är gratis att se, senaste säsongen först.
              </Typography>
            )}
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Börja skriva för att söka..."
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: isSearching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {/* Side by Side: Search Results and Selected Videos */}
          <Box sx={{ display: 'flex', gap: 2, minHeight: 400 }}>
            {/* Search Results - Left Side */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Sökresultat {searchResults.length > 0 && `(${searchResults.length})`}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: 360,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  overflowY: 'auto',
                  bgcolor: 'grey.50',
                }}
              >
                {searchResults.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body2">
                      {searchQuery.trim() ? 'Inga resultat' : 'Sök för att hitta videor'}
                    </Typography>
                  </Box>
                ) : (
                  searchResults.map((video) => {
                    const createdDate = getVideoCreatedDate(video);

                    return (
                      <Paper
                        key={video.id}
                        elevation={0}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box
                          component="img"
                          src={video.thumbnail}
                          alt={video.title}
                          sx={{
                            width: 80,
                            height: 45,
                            objectFit: 'cover',
                            borderRadius: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {video.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {video.duration}
                            </Typography>
                            {createdDate && (
                              <Typography variant="caption" color="text.secondary">
                                {createdDate}
                              </Typography>
                            )}
                            {video.channelName && (
                              <Chip
                                label={video.channelName}
                                size="small"
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            )}
                            {video.category && (
                              <Chip
                                label={video.category}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => setDetailsVideo(video)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' },
                            width: 28,
                            height: 28,
                            flexShrink: 0,
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleAddVideo(video)}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                            width: 28,
                            height: 28,
                            flexShrink: 0,
                          }}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Paper>
                    );
                  })
                )}
              </Paper>
            </Box>

            {/* Selected Videos - Right Side */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Valda videor ({selectedVideos.length})
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: 360,
                  bgcolor: 'background.paper',
                  border: '2px dashed',
                  borderColor: selectedVideos.length > 0 ? 'primary.300' : 'grey.300',
                  borderRadius: 2,
                  overflowY: 'auto',
                }}
              >
                {selectedVideos.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body2" textAlign="center">
                      Klicka på + för att lägga till videor
                    </Typography>
                  </Box>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={selectedVideos.map((v) => v.id)}
                      strategy={horizontalListSortingStrategy}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {selectedVideos.map((video, index) => (
                          <SortableVideoRow
                            key={video.id}
                            video={video}
                            index={index}
                            onRemove={() => handleRemoveVideo(video.id)}
                          />
                        ))}
                      </Box>
                    </SortableContext>
                    <DragOverlay>
                      {activeVideo ? <VideoRowOverlay video={activeVideo} /> : null}
                    </DragOverlay>
                  </DndContext>
                )}
              </Paper>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Avbryt
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!title.trim() || selectedVideos.length === 0}
        >
          {editCarousel ? 'Spara ändringar' : 'Skapa karusell'}
        </Button>
      </DialogActions>

      {/* Video Details Modal */}
      <VideoDetailsModal
        open={!!detailsVideo}
        onClose={() => setDetailsVideo(null)}
        video={detailsVideo}
      />
    </Dialog>
  );
}

export default VideoCarouselModal;
